import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Container, PageTitle, NbCard, Chip } from "@/components/nb";
import { CheckCircle2, Circle, PlayCircle, X, Search } from "lucide-react";

export default function SkillTrack() {
  const { id } = useParams();
  const { user } = useAuth();
  const [track, setTrack] = useState(null);
  const [progress, setProgress] = useState({});
  const [openLesson, setOpenLesson] = useState(null);
  const [q, setQ] = useState("");

  const loadProgress = () => {
    if (!user) return;
    api.get("/skills/progress").then((r) => {
      const map = {};
      r.data.forEach((p) => { if (p.track_id === id) map[p.project_id] = p.completed; });
      setProgress(map);
    });
  };

  useEffect(() => {
    api.get(`/skills/tracks/${id}`).then((r) => setTrack(r.data));
    loadProgress();
    // eslint-disable-next-line
  }, [id, user]);

  const toggle = async (pid) => {
    if (!user) return alert("Sign in to save progress");
    const next = !progress[pid];
    setProgress({ ...progress, [pid]: next });
    await api.post("/skills/progress", { track_id: id, project_id: pid, completed: next });
  };

  if (!track) return <Container><div>Loading...</div></Container>;

  const query = q.trim().toLowerCase();
  const filteredLessons = query ? track.lessons.filter((l) => (l.title || "").toLowerCase().includes(query) || (l.teacher || "").toLowerCase().includes(query)) : track.lessons;
  const filteredProjects = query ? track.projects.filter((p) => (p.title || "").toLowerCase().includes(query)) : track.projects;

  return (
    <Container>
      <PageTitle eyebrow={`SKILL TRACK · ${track.title.toUpperCase()}`} title={track.title} sub={track.description} />
      <div className="flex items-center gap-3 bg-white nb-border nb-shadow rounded-xl px-4 py-3 max-w-xl mb-6">
        <Search className="w-5 h-5 shrink-0" />
        <input data-testid="lesson-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lessons or projects..." className="flex-1 bg-transparent outline-none py-1" />
        {q && <button onClick={() => setQ("")} data-testid="clear-lesson" className="font-mono-sl text-[10px] uppercase underline">Clear</button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <NbCard className="!p-6">
          <h3 className="font-heading font-black text-2xl tracking-tight">Video Lessons</h3>
          <p className="text-sm text-neutral-600 mt-1">Handpicked from the world's best teachers.</p>
          <ul className="mt-4 space-y-3" data-testid="lessons">
            {filteredLessons.length === 0 && <li className="text-neutral-500 text-sm">No lessons match.</li>}
            {filteredLessons.map((l) => (
              <li key={l.id}>
                <button onClick={() => setOpenLesson(l)} data-testid={`lesson-${l.id}`} className="w-full text-left flex items-center gap-3 nb-border rounded-lg px-4 py-3 bg-white nb-hover">
                  <PlayCircle className="w-6 h-6 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold truncate">{l.title}</div>
                    <div className="text-xs text-neutral-600">{l.duration} · by {l.teacher}</div>
                  </div>
                  <Chip color="tertiary">WATCH</Chip>
                </button>
              </li>
            ))}
          </ul>
        </NbCard>

        <NbCard color={track.color} className="!p-6">
          <h3 className="font-heading font-black text-2xl tracking-tight">Hands-on Projects</h3>
          <p className="text-sm text-neutral-800 mt-1">Ship one this week.</p>
          <ul className="mt-4 space-y-3" data-testid="projects">
            {filteredProjects.length === 0 && <li className="text-neutral-500 text-sm">No projects match.</li>}
            {filteredProjects.map((p) => (
              <li key={p.id}>
                <button onClick={() => toggle(p.id)} data-testid={`proj-${p.id}`} className="w-full flex items-center gap-3 bg-white nb-border rounded-lg px-4 py-3 nb-hover">
                  {progress[p.id] ? <CheckCircle2 className="w-5 h-5 text-green-700" /> : <Circle className="w-5 h-5" />}
                  <span className={`font-medium ${progress[p.id] ? "line-through opacity-70" : ""}`}>{p.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </NbCard>
      </div>

      {openLesson && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-5" onClick={() => setOpenLesson(null)} data-testid="lesson-modal">
          <div className="w-full max-w-4xl bg-white nb-border nb-shadow-xl rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b-2 border-black flex justify-between items-center gap-3">
              <div className="min-w-0">
                <div className="font-heading font-bold truncate">{openLesson.title}</div>
                <div className="font-mono-sl text-[10px] uppercase text-neutral-600">by {openLesson.teacher} · {openLesson.duration}</div>
              </div>
              <button onClick={() => setOpenLesson(null)} data-testid="close-lesson" className="p-2 nb-border rounded-lg bg-white nb-hover">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="aspect-video bg-black">
              <iframe title={openLesson.title} className="w-full h-full" src={`https://www.youtube.com/embed/${openLesson.youtube_id}?rel=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}
